import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, Form, Container, Pagination, Modal, Button, Row, Col, ProgressBar as Progress, Alert, Accordion } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import DriverSelector from "../../components/driverSelector";
import WeekSelector from "../weekSelector";
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineMinus, AiOutlineTeam, AiOutlineUnorderedList } from "react-icons/ai";
import { useTranslation } from 'react-i18next';

const getPrevWeekNumber = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const firstMonday = new Date(currentYear, 0, 1);
    while (firstMonday.getDay() !== 1) firstMonday.setDate(firstMonday.getDate() + 1);
    const prevWeekDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const weekStart = new Date(firstMonday);
    let currentWeekNumber = 1;
    while (weekStart <= prevWeekDate) {
        weekStart.setDate(weekStart.getDate() + 7);
        currentWeekNumber++;
    }
    return `${currentYear}${String(currentWeekNumber - 1).padStart(2, "0")}`;
};

const createForAllFieldOptions = [
    { key: "returnVat", labelKey: "vat", fallback: "VAT" },
    { key: "taxesZus", labelKey: "zus", fallback: "ZUS" },
    { key: "partner", labelKey: "partner", fallback: "Partner" },
    { key: "accountant", labelKey: "accountant", fallback: "Accountant" },
    { key: "carRent", labelKey: "carRent", fallback: "Car rent" },
    { key: "sallary", labelKey: "sallary", fallback: "Salary" },
    { key: "otherExpenses", labelKey: "otherCosts", fallback: "Other costs" },
    { key: "refundsItems", labelKey: "refunds", fallback: "Refunds" },
];

const defaultCreateForAllFields = createForAllFieldOptions.reduce((acc, option) => {
    acc[option.key] = true;
    return acc;
}, {});

const parseMoneyItems = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value || "[]");
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const getFundField = (fund, field) => {
    if (field === "taxesZus") return fund.taxesZus ?? fund.taxesZUS ?? "";
    if (field === "returnVat") return fund.returnVat ?? fund.returnVAT ?? "";
    return fund[field];
};

const toExtraFundsPayload = (fund, overrides = {}) => {
    const merged = { ...fund, ...overrides };
    return {
        driver_id: merged.driver_id,
        week_number: merged.week_number,
        returnVat: merged.returnVat ?? merged.returnVAT ?? 0,
        taxesZus: merged.taxesZus ?? merged.taxesZUS ?? 0,
        partner: merged.partner ?? 0,
        accountant: merged.accountant ?? 0,
        carRent: merged.carRent ?? 0,
        sallary: merged.sallary ?? 0,
        other: merged.other ?? 0,
        otherExpenses: parseMoneyItems(merged.otherExpenses ?? merged.other_expenses),
        refunds: merged.refunds ?? 0,
        refundsDesc: typeof merged.refundsDesc === "string"
            ? merged.refundsDesc
            : JSON.stringify(parseMoneyItems(merged.refundsDesc)),
    };
};

const ExtraFunds = () => {
    const { t } = useTranslation();
    const [funds, setFunds] = useState([]);
    const [allDebts, setAllDebts] = useState([]);
    const [filters, setFilters] = useState({ user_id: null });
    const [selectedWeek, setSelectedWeek] = useState(() => getPrevWeekNumber());
    const [editCell, setEditCell] = useState({ id: null, field: null });
    const [editedValue, setEditedValue] = useState("");
    const navigate = useNavigate();
    const [onlyCurrentWeek, setOnlyCurrentWeek] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [lastClickedIndex, setLastClickedIndex] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 40;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const [skipBlur, setSkipBlur] = useState(false);
    const [showDriverDebtsModal, setShowDriverDebtsModal] = useState(false);
    const [driverDebtsModalDriverId, setDriverDebtsModalDriverId] = useState(null);
    const [createForAllOverlay, setCreateForAllOverlay] = useState(false);
    const [createForAllProgress, setCreateForAllProgress] = useState(0);
    const [createForAllCurrent, setCreateForAllCurrent] = useState(0);
    const [createForAllTotal, setCreateForAllTotal] = useState(0);
    const [createForAllCurrentDriverName, setCreateForAllCurrentDriverName] = useState("");
    const [showCreateForAllOptionsModal, setShowCreateForAllOptionsModal] = useState(false);
    const [createForAllFields, setCreateForAllFields] = useState(defaultCreateForAllFields);
    const [foldPreview, setFoldPreview] = useState(null);
    const [foldPreviewLoading, setFoldPreviewLoading] = useState(false);
    const [foldPreviewError, setFoldPreviewError] = useState(null);

    const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }), []);

    const fetchFunds = useCallback(async () => {
        try {
            const params = {};
            if (selectedWeek) params.week_number = selectedWeek;
            if (filters.user_id?.value) params.driver_id = filters.user_id.value;
            const response = await axios.get("/api/extraFunds", { ...authHeaders(), params });
            const sortedFunds = (response.data || []).sort((a, b) => {
                const wa = String(a.week_number || "");
                const wb = String(b.week_number || "");
                if (wa !== wb) return wb.localeCompare(wa);
                const na = `${a.lastName || ""} ${a.firstName || ""}`;
                const nb = `${b.lastName || ""} ${b.firstName || ""}`;
                return na.localeCompare(nb);
            });
            setFunds(sortedFunds);
        } catch (error) {
            console.error("Error fetching extra funds", error);
        }
    }, [selectedWeek, filters.user_id, authHeaders]);

    useEffect(() => {
        fetchFunds();
    }, [fetchFunds]);

    useEffect(() => {
        axios.get("/api/drivers-debts", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
            .then((res) => setAllDebts(res.data || []))
            .catch((err) => console.error("Помилка завантаження боргів:", err));
    }, []);

    useEffect(() => {
        if (!showCreateForAllOptionsModal || !createForAllFields.returnVat) {
            return;
        }
        let cancelled = false;
        setFoldPreviewLoading(true);
        setFoldPreviewError(null);
        axios.get("/api/driver-invoices/fold-preview", authHeaders())
            .then((res) => {
                if (!cancelled) setFoldPreview(res.data || null);
            })
            .catch((err) => {
                if (!cancelled) {
                    setFoldPreview(null);
                    setFoldPreviewError(err?.response?.data?.error || t("errorFoldVatPreview") || "Не вдалося завантажити прев'ю VAT.");
                }
            })
            .finally(() => {
                if (!cancelled) setFoldPreviewLoading(false);
            });
        return () => { cancelled = true; };
    }, [showCreateForAllOptionsModal, createForAllFields.returnVat, authHeaders, t]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedWeek, filters.user_id]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setSelectedRows([]);
                setLastClickedIndex(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);


    const handleDoubleClick = (id, field, currentValue) => {
        setEditCell({ id, field });
        setEditedValue(currentValue);
    };

    const handleRowClick = (e, index, id) => {
        if (e.shiftKey && lastClickedIndex !== null) {
            const [start, end] = [lastClickedIndex, index].sort((a, b) => a - b);
            const rangeIds = filteredFunds.slice(start, end + 1).map(f => f.id);
            setSelectedRows(prev => Array.from(new Set([...prev, ...rangeIds])));
        } else if (e.ctrlKey || e.metaKey) {
            setSelectedRows(prev =>
                prev.includes(id)
                    ? prev.filter(rowId => rowId !== id)
                    : [...prev, id]
            );
            setLastClickedIndex(index);
        }
    };


    const handleInputChange = (e) => {
        setEditedValue(e.target.value);
    };

    const handleBlur = async () => {
        const idsToUpdate = selectedRows.length > 1 ? selectedRows : [editCell.id];
        const isZero = (val) => parseFloat(val) === 0 || val === "" || val === null;
        const fieldsToCheck = ["returnVat", "taxesZus", "partner", "accountant", "carRent", "sallary", "other", "refunds"];

        try {
            // 🔸 Перевірка all-zero тільки якщо один запис
            if (idsToUpdate.length === 1) {
                const id = idsToUpdate[0];
                const fundToUpdate = funds.find(f => f.id === id);
                if (fundToUpdate) {
                    const updatedFund = { ...fundToUpdate, [editCell.field]: editedValue.replace(',', '.') };
                    const allZero = fieldsToCheck.every(field => {
                        const value = field === editCell.field
                            ? updatedFund[field]
                            : getFundField(fundToUpdate, field);
                        return isZero(value);
                    });

                    if (allZero) {
                        const confirmDelete = window.confirm(
                            t("confirmDeleteZeroRecord") || "Усі значення дорівнюють 0. Бажаєте видалити запис?"
                        );
                        if (confirmDelete) {
                            // 👇 ключовий момент — затримка
                            setTimeout(async () => {
                                try {
                                    await axios.delete(`/api/extraFunds/${id}`, authHeaders());
                                    await fetchFunds();
                                } catch (error) {
                                    console.error("Помилка при видаленні:", error);
                                }
                            }, 0);
                        }
                        setEditCell({ id: null, field: null });
                        return;
                    }
                }
            }

            // 🔸 Масове або звичайне оновлення
            await Promise.all(
                idsToUpdate.map(id => {
                    const fundToUpdate = funds.find(f => f.id === id);
                    if (!fundToUpdate) return null;

                    const updatedData = toExtraFundsPayload(fundToUpdate, {
                        [editCell.field]: editedValue.replace(',', '.'),
                    });
                    return axios.put(`/api/extraFunds/${id}`, updatedData, authHeaders());
                })
            );

            await fetchFunds();
        } catch (error) {
            console.error("Помилка масового оновлення:", error);
            alert(error.response?.data?.error || t("errorSaving") || "Помилка збереження запису.");
        }

        setEditCell({ id: null, field: null });
    };





    const handleEdit = (id) => {
        navigate(`/admin/edit-extra-funds/${id}`);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("deleteQuestion"))) return;

        try {
            await axios.delete(`/api/extraFunds/${id}`, authHeaders());
            fetchFunds();
        } catch (error) {
            console.error("Error deleting extra fund", error);
            alert(t("deleteFalseMessage"));
        }
    };

    const filteredFunds = funds.filter((fund) => {
        const matchDriver = filters.user_id ? String(fund.driver_id) === String(filters.user_id.value) : true;
        const matchWeek = !selectedWeek || String(fund.week_number) === String(selectedWeek);
        return matchDriver && matchWeek;
    });

    const currentItems = filteredFunds.slice(indexOfFirstItem, indexOfLastItem);

    const getDriverOpenDebts = (driverId) =>
        (allDebts || []).filter((d) => String(d.driver_id) === String(driverId) && d.debt_status !== "closed");

    const openDebtsModal = (driverId, e) => {
        if (e) e.stopPropagation();
        setDriverDebtsModalDriverId(driverId);
        setShowDriverDebtsModal(true);
    };

    const driverDebtsModalDriverName = driverDebtsModalDriverId != null
        ? (() => {
            const f = funds.find((x) => String(x.driver_id) === String(driverDebtsModalDriverId));
            return f ? `${f.firstName} ${f.lastName}` : "";
        })()
        : "";
    const driverDebtsList = driverDebtsModalDriverId != null
        ? allDebts
            .filter((d) => String(d.driver_id) === String(driverDebtsModalDriverId) && d.debt_status !== "closed")
            .sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : new Date(a.year || 0, 0, (a.week_number || 0) * 7);
                const dateB = b.created_at ? new Date(b.created_at) : new Date(b.year || 0, 0, (b.week_number || 0) * 7);
                return dateB - dateA;
            })
        : [];

    const isNonZero = (v) => {
        const n = parseFloat(String(v || "").replace(",", "."));
        return !Number.isNaN(n) && n !== 0;
    };

    const handleCreateForAllWithDefaults = () => {
        if (!selectedWeek) {
            alert(t("chooseWeek") || t("selectWeek") || "Оберіть тиждень");
            return;
        }
        setShowCreateForAllOptionsModal(true);
    };

    const toggleCreateForAllField = (field) => {
        setCreateForAllFields((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const setAllCreateForAllFields = (checked) => {
        setCreateForAllFields(
            createForAllFieldOptions.reduce((acc, option) => {
                acc[option.key] = checked;
                return acc;
            }, {})
        );
    };

    const executeCreateForAllWithDefaults = async () => {
        const selectedMergeFields = createForAllFieldOptions
            .filter((option) => createForAllFields[option.key])
            .map((option) => option.key);

        if (selectedMergeFields.length === 0) {
            alert(t("selectAtLeastOneValue") || "Оберіть хоча б одне значення для застосування.");
            return;
        }

        const applyField = (field) => selectedMergeFields.includes(field);
        const config = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

        // ВАТ обробляється окремо від стандартних значень: сюди потрапляють підтверджені
        // (approved) фактури водіїв незалежно від наявності стандартних значень дод. коштів.
        // Тому "returnVat" виключаємо з циклу за дефолтами нижче.
        let foldResult = null;
        if (applyField("returnVat")) {
            try {
                const foldRes = await axios.post(
                    "/api/driver-invoices/fold-into-extra-funds",
                    { week_number: selectedWeek },
                    config
                );
                foldResult = foldRes.data;
            } catch (foldErr) {
                console.error("Fold VAT into extra funds:", foldErr);
                alert(t("errorFoldVat") || "Не вдалося включити підтверджені фактури у додаткові кошти.");
                return;
            }
        }

        const loopMergeFields = selectedMergeFields.filter((field) => field !== "returnVat");
        const loopApplyField = (field) => loopMergeFields.includes(field);

        const foldSummaryMsg = () => {
            if (!foldResult) return "";
            const totals = foldResult.totals || {};
            const drivers = Array.isArray(foldResult.drivers) ? foldResult.drivers : [];
            if (!foldResult.processedDrivers) {
                return `\n\n${t("foldVatNone") || "Підтверджених фактур для включення у дод. кошти не знайдено."}`;
            }
            const header = t("foldVatDoneHeader", {
                invoices: totals.invoices ?? foldResult.invoicesCount ?? 0,
                drivers: totals.drivers ?? foldResult.processedDrivers ?? 0,
                vat: Number(totals.vat ?? 0).toFixed(2),
                refund: Number(totals.refund ?? foldResult.totalAmount ?? 0).toFixed(2),
                week: selectedWeek,
            });
            const lines = drivers.map((d) =>
                `${d.driver_name}: ${d.invoices_count} fv, VAT ${Number(d.total_vat).toFixed(2)}, zwrot ${Number(d.total_refund).toFixed(2)}`
            );
            return `\n\n${header}\n${lines.join("\n")}`;
        };

        // Якщо обрано лише ВАТ (без інших стандартних значень) — цикл по дефолтах не потрібен.
        if (loopMergeFields.length === 0) {
            setShowCreateForAllOptionsModal(false);
            alert(`${t("done") || "Готово"}.${foldSummaryMsg()}`);
            fetchFunds();
            return;
        }

        try {
            const res = await axios.get("/api/extra-funds-default-values", config);
            const allDefaults = res.data || [];
            const hasSelectedNonEmptyDefaults = (row) => {
                if (loopApplyField("taxesZus") && isNonZero(row.taxesZus)) return true;
                if (loopApplyField("partner") && isNonZero(row.partner)) return true;
                if (loopApplyField("accountant") && isNonZero(row.accountant)) return true;
                if (loopApplyField("carRent") && isNonZero(row.carRent)) return true;
                if (loopApplyField("sallary") && isNonZero(row.sallary)) return true;
                const otherExpenses = Array.isArray(row.otherExpenses) ? row.otherExpenses : [];
                const refundsItems = Array.isArray(row.refundsItems) ? row.refundsItems : [];
                if (
                    loopApplyField("otherExpenses") &&
                    otherExpenses.some((e) => (e?.amount ?? "").toString().trim() !== "" || (e?.description ?? "").toString().trim() !== "")
                ) return true;
                if (
                    loopApplyField("refundsItems") &&
                    refundsItems.some((r) => (r?.amount ?? "").toString().trim() !== "" || (r?.description ?? "").toString().trim() !== "")
                ) return true;
                return false;
            };
            const withNonEmpty = allDefaults.filter(hasSelectedNonEmptyDefaults);
            if (withNonEmpty.length === 0) {
                setShowCreateForAllOptionsModal(false);
                alert(`${t("noDriversWithDefaults") || "Немає водіїв з непорожніми стандартними значеннями."}${foldSummaryMsg()}`);
                fetchFunds();
                return;
            }
            const confirmMsg = t("areYouSureCreateForAllWithDefaults") || `Створити записи для ${withNonEmpty.length} водіїв(я) з непорожніми стандартними значеннями на обраний тиждень?`;
            if (!window.confirm(confirmMsg)) return;
            setShowCreateForAllOptionsModal(false);

            const total = withNonEmpty.length;
            setCreateForAllTotal(total);
            setCreateForAllCurrent(0);
            setCreateForAllProgress(0);
            setCreateForAllCurrentDriverName("");
            setCreateForAllOverlay(true);

            const driverName = (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || String(row.driver_id);

            let processed = 0;
            try {
                for (const row of withNonEmpty) {
                    setCreateForAllCurrentDriverName(driverName(row));
                    const otherExpenses = loopApplyField("otherExpenses") && Array.isArray(row.otherExpenses) ? row.otherExpenses : [];
                    const refundsItems = loopApplyField("refundsItems") && Array.isArray(row.refundsItems) ? row.refundsItems : [];
                    const totalOther = otherExpenses.reduce((sum, e) => sum + (parseFloat(e?.amount) || 0), 0);
                    const totalRefunds = refundsItems.reduce((sum, r) => sum + (parseFloat(r?.amount) || 0), 0);
                    try {
                        await axios.post(
                            "/api/extraFunds",
                            {
                                driver_id: row.driver_id,
                                week_number: selectedWeek,
                                returnVat: 0,
                                taxesZus: loopApplyField("taxesZus") ? parseFloat(row.taxesZus) || 0 : 0,
                                partner: loopApplyField("partner") ? parseFloat(row.partner) || 0 : 0,
                                accountant: loopApplyField("accountant") ? parseFloat(row.accountant) || 0 : 0,
                                carRent: loopApplyField("carRent") ? parseFloat(row.carRent) || 0 : 0,
                                sallary: loopApplyField("sallary") ? parseFloat(row.sallary) || 0 : 0,
                                other: totalOther,
                                otherExpenses,
                                refunds: totalRefunds,
                                refundsDesc: JSON.stringify(refundsItems),
                                mergeIfExists: true,
                                mergeFields: loopMergeFields,
                            },
                            config
                        );
                    } catch (postErr) {
                        if (postErr.response?.status === 409) { /* skip */ }
                        else throw postErr;
                    }
                    processed++;
                    setCreateForAllCurrent(processed);
                    setCreateForAllProgress(Math.round((processed / total) * 100));
                    await new Promise((r) => setTimeout(r, 700));
                }
                fetchFunds();
                if (foldResult) alert(`${t("done") || "Готово"}.${foldSummaryMsg()}`);
            } catch (err) {
                console.error("Create for all (defaults):", err);
                alert(t("errorCreateForAll") || "Помилка при створенні записів.");
            } finally {
                setCreateForAllOverlay(false);
            }
        } catch (err) {
            console.error("Create for all (defaults):", err);
            alert(t("errorCreateForAll") || "Помилка при створенні записів.");
            setCreateForAllOverlay(false);
        }
    };

    return (
        <>
            {createForAllOverlay && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        zIndex: 1050,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div className="bg-white rounded shadow-lg p-4" style={{ minWidth: "320px" }}>
                        <div className="mb-3 fw-bold">
                            {t("extraCosts")} — {t("createForAllUsers")}
                        </div>
                        <div className="mb-2 small text-muted">
                            {createForAllCurrent} / {createForAllTotal}
                        </div>
                        {createForAllCurrentDriverName && (
                            <div className="mb-2 fw-medium text-dark">
                                {createForAllCurrentDriverName}
                            </div>
                        )}
                        <Progress now={createForAllProgress} label={`${createForAllProgress}%`} style={{ height: "24px" }} />
                    </div>
                </div>
            )}
            <Modal
                show={showCreateForAllOptionsModal}
                onHide={() => setShowCreateForAllOptionsModal(false)}
                centered
                size="xl"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("createForAllUsers")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        {t("selectValuesToApply") || "Оберіть, які стандартні значення застосувати. Для нових записів невибрані значення будуть 0, в існуючих записах вони не зміняться."}
                    </p>
                    <div className="d-flex gap-2 mb-3">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setAllCreateForAllFields(true)}
                        >
                            {t("selectAll") || "Обрати всі"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setAllCreateForAllFields(false)}
                        >
                            {t("clearAll") || "Очистити все"}
                        </Button>
                    </div>
                    <div className="row g-2 mb-3">
                        {createForAllFieldOptions.map((option) => (
                            <div className="col-12 col-sm-6" key={option.key}>
                                <Form.Check
                                    type="checkbox"
                                    id={`create-for-all-${option.key}`}
                                    label={t(option.labelKey) || option.fallback}
                                    checked={!!createForAllFields[option.key]}
                                    onChange={() => toggleCreateForAllField(option.key)}
                                />
                            </div>
                        ))}
                    </div>

                    {createForAllFields.returnVat ? (
                        <div className="border rounded p-3 bg-light">
                            <h6 className="mb-2">{t("foldVatPreviewTitle")}</h6>
                            {foldPreviewLoading && (
                                <div className="small text-muted">{t("ksefLoading")}</div>
                            )}
                            {foldPreviewError && (
                                <Alert variant="danger" className="py-2 mb-0">{foldPreviewError}</Alert>
                            )}
                            {!foldPreviewLoading && !foldPreviewError && foldPreview && (
                                <>
                                    <Alert variant="info" className="py-2">
                                        {t("foldVatPreviewTotals", {
                                            week: selectedWeek || "—",
                                            invoices: foldPreview.totals?.invoices || 0,
                                            drivers: foldPreview.totals?.drivers || 0,
                                            vat: Number(foldPreview.totals?.vat || 0).toFixed(2),
                                            refund: Number(foldPreview.totals?.refund || 0).toFixed(2),
                                        })}
                                    </Alert>
                                    {(foldPreview.drivers || []).length === 0 ? (
                                        <p className="small text-muted mb-0">{t("foldVatNone")}</p>
                                    ) : (
                                        <Accordion alwaysOpen>
                                            {(foldPreview.drivers || []).map((driver, idx) => (
                                                <Accordion.Item eventKey={String(driver.driver_id || idx)} key={driver.driver_id || idx}>
                                                    <Accordion.Header>
                                                        <span className="d-flex flex-wrap gap-2 align-items-center">
                                                            <strong>{driver.driver_name}</strong>
                                                            <span className="small text-muted">
                                                                {t("foldVatDriverSummary", {
                                                                    count: driver.invoices_count,
                                                                    vat: Number(driver.total_vat).toFixed(2),
                                                                    refund: Number(driver.total_refund).toFixed(2),
                                                                })}
                                                            </span>
                                                        </span>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="p-0">
                                                        <Table striped bordered size="sm" className="mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>{t("ksefTableDate")}</th>
                                                                    <th>{t("ksefTableNumber")}</th>
                                                                    <th className="text-end">VAT</th>
                                                                    <th className="text-end">{t("refunds")}</th>
                                                                    <th>{t("description") || t("note") || "Опис"}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(driver.invoices || []).map((inv) => (
                                                                    <tr key={inv.id}>
                                                                        <td>{inv.issue_date || "—"}</td>
                                                                        <td>{inv.invoice_number || inv.ksef_number || `#${inv.id}`}</td>
                                                                        <td className="text-end">{Number(inv.vat_amount).toFixed(2)}</td>
                                                                        <td className="text-end">{Number(inv.refund_amount).toFixed(2)}</td>
                                                                        <td className="small">{inv.note || "—"}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <Alert variant="warning" className="mb-0">
                            {t("foldVatUncheckedHint")}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCreateForAllOptionsModal(false)}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={executeCreateForAllWithDefaults}
                        disabled={!createForAllFieldOptions.some((option) => createForAllFields[option.key])}
                    >
                        {t("apply") || t("create")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Container>
            <h2>{t("extraCosts")}</h2>
            <Form>
                <Row className="g-2 align-items-stretch">
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("driver")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <DriverSelector
                                    selectedDriver={filters.user_id}
                                    setSelectedDriver={(selectedOption) => setFilters({ ...filters, user_id: selectedOption })}
                                    className="extra-funds-driver-select"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Group className="mb-0 w-100 d-flex flex-column">
                            <Form.Label className="small mb-1 flex-shrink-0" style={{ minHeight: "20px", lineHeight: "20px" }}>{t("week")}</Form.Label>
                            <div className="w-100 flex-grow-1" style={{ height: "38px" }}>
                                <WeekSelector
                                    selectedWeek={selectedWeek}
                                    setSelectedWeek={setSelectedWeek}
                                    className="mb-0 w-100"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
            <Row className="mt-4 g-2">
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        as="a"
                        href="/admin/edit-extra-funds"
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                        <AiOutlinePlus size={18} />
                        {t("create")}
                    </Button>
                </Col>
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={handleCreateForAllWithDefaults}
                    >
                        <AiOutlineTeam size={18} />
                        {t("createForAllUsers")}
                    </Button>
                </Col>
                <Col xs={12} md={selectedRows.length > 0 ? 3 : 4}>
                    <Button
                        as="a"
                        href="/admin/extra-funds-default-values"
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                        <AiOutlineUnorderedList size={18} />
                        {t("standardValues")}
                    </Button>
                </Col>
                {selectedRows.length > 0 && (
                    <Col xs={12} md={3}>
                        <Button
                            variant="outline-danger"
                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={async () => {
                                if (window.confirm(t("confirmdelete"))) {
                                    try {
                                        await Promise.all(selectedRows.map(id =>
                                            axios.delete(`/api/extraFunds/${id}`, authHeaders())
                                        ));
                                        fetchFunds();
                                        setSelectedRows([]);
                                    } catch (error) {
                                        console.error("Error deleting selected:", error);
                                    }
                                }
                            }}
                        >
                            <AiOutlineDelete size={18} />
                            {t("deleteSelected")}
                        </Button>
                    </Col>
                )}
            </Row>



            <h3 className="mt-4">{t("listOf")} {t("extraCosts").toLocaleLowerCase()}</h3>
            <Table striped bordered hover style={{ tableLayout: "fixed", width: "100%" }}>
                <thead className="sticky-top">
                    <tr>
                        <th style={{ width: "14%" }}>{t("driver")}</th>
                        <th style={{ width: "7%" }}>{t("week")}</th>
                        <th style={{ width: "8%" }}>{t("vat")}</th>
                        <th style={{ width: "8%" }}>{t("zus")}</th>
                        <th style={{ width: "8%" }}>{t("partner")}</th>
                        <th style={{ width: "8%" }}>{t("accountant")}</th>
                        <th style={{ width: "8%" }}>{t("carRent")}</th>
                        <th style={{ width: "8%" }}>{t("sallary")}</th>
                        <th style={{ width: "8%" }}>{t("debtsMulti")}</th>
                        <th style={{ width: "8%" }}>{t("other")}</th>
                        <th style={{ width: "8%" }}>{t("refunds")}</th>
                        <th style={{ width: "7%" }}>{t("actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentItems.map((fund, index) => (
                        <tr
                            key={fund.id}
                            onClick={(e) => handleRowClick(e, index + indexOfFirstItem, fund.id)}
                            className={selectedRows.includes(fund.id) ? "table-primary" : ""}
                        >
                            <td>{fund.firstName} {fund.lastName}</td>
                            <td>{fund.week_number}</td>
                            {["returnVat", "taxesZus", "partner", "accountant", "carRent", "sallary", "other", "refunds"].map(field => (
                                <React.Fragment key={field}>
                                    <td
                                        className="align-middle position-relative text-center text-truncate"
                                        style={{ width: "8%" }}
                                        onDoubleClick={() => {
                                            if (field !== "other" && field !== "refunds") {
                                                handleDoubleClick(fund.id, field, getFundField(fund, field));
                                            }
                                        }}
                                    >
                                        {editCell.id === fund.id && editCell.field === field ? (
                                            <>
                                                <span className="invisible text-nowrap" aria-hidden>
                                                    {getFundField(fund, field) ?? ""}
                                                </span>
                                                <Form.Control
                                                    type="text"
                                                    className="position-absolute top-50 start-0 end-0 translate-middle-y mx-1"
                                                    style={{ width: "auto", maxWidth: "calc(100% - 0.5rem)" }}
                                                    value={editedValue}
                                                    onChange={handleInputChange}
                                                    onBlur={() => {
                                                        if (!skipBlur) handleBlur();
                                                        else setSkipBlur(false); // збити флаг
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleBlur();
                                                        }
                                                    }}
                                                    autoFocus
                                                    size="sm"
                                                />
                                            </>
                                        ) : (
                                            getFundField(fund, field)
                                        )}
                                    </td>
                                    {field === "sallary" && (
                                        <td
                                            className="text-nowrap text-center"
                                            style={{ width: "8%", cursor: "pointer", verticalAlign: "middle" }}
                                            onClick={(e) => openDebtsModal(fund.driver_id, e)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === "Enter" && openDebtsModal(fund.driver_id, e)}
                                        >
                                            {(() => {
                                                const open = getDriverOpenDebts(fund.driver_id);
                                                const total = open.reduce((sum, d) => sum + (parseFloat(d.debt_total) || 0), 0);
                                                if (total === 0) {
                                                    return (
                                                        <span className="d-inline-flex align-items-center justify-content-center">
                                                            <AiOutlineMinus
                                                                className="text-success"
                                                                size={20}
                                                                title={t("noDebts") || "Боргів немає"}
                                                            />
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <span className="text-danger fw-bold">
                                                        {total.toFixed(2)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                            <td>
                                <AiOutlineEdit
                                    style={{ cursor: "pointer", marginRight: "10px", color: "orange" }}
                                    size={20}
                                    onClick={() => handleEdit(fund.id)}
                                />
                                <AiOutlineDelete
                                    style={{ cursor: "pointer", color: "red" }}
                                    size={20}
                                    onClick={() => handleDelete(fund.id)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {filteredFunds.length > itemsPerPage && (
                <Pagination className="justify-content-center mt-4">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />

                    {Array.from({ length: Math.ceil(filteredFunds.length / itemsPerPage) }, (_, i) => (
                        <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                        >
                            {i + 1}
                        </Pagination.Item>
                    ))}

                    <Pagination.Next
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredFunds.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(filteredFunds.length / itemsPerPage)}
                    />
                    <Pagination.Last
                        onClick={() => setCurrentPage(Math.ceil(filteredFunds.length / itemsPerPage))}
                        disabled={currentPage === Math.ceil(filteredFunds.length / itemsPerPage)}
                    />
                </Pagination>
            )}

            {/* Модальне вікно: активні борги водія */}
            <Modal show={showDriverDebtsModal} onHide={() => setShowDriverDebtsModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("debtsMulti")} — {driverDebtsModalDriverName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {driverDebtsList.length === 0 ? (
                        <p className="p-4 text-muted mb-0">{t("noRecords")}</p>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {driverDebtsList.map((debt) => (
                                <li
                                    key={debt.id}
                                    className="list-group-item d-flex align-items-start justify-content-between gap-3 py-3"
                                    style={{ borderLeft: `3px solid ${debt.debt_status === "closed" ? "var(--bs-secondary)" : "var(--bs-danger)"}` }}
                                >
                                    <div className="d-flex flex-column min-w-0 flex-grow-1">
                                        <Link
                                            to={`/admin/driver-debts/${debt.id}`}
                                            className={`text-break text-decoration-none ${debt.debt_status !== "closed" ? "fw-bold text-danger" : "fw-semibold text-dark"}`}
                                            onClick={() => setShowDriverDebtsModal(false)}
                                        >
                                            {debt.debt_name}
                                        </Link>
                                        {(debt.debt_create_desc || debt.week_number != null) && (
                                            <span className="small text-muted mt-1 text-break">
                                                {debt.week_number != null && `${t("week")} ${debt.week_number}`}
                                                {debt.week_number != null && debt.debt_create_desc && " · "}
                                                {debt.debt_create_desc && (debt.debt_create_desc.length > 80 ? debt.debt_create_desc.slice(0, 80) + "…" : debt.debt_create_desc)}
                                            </span>
                                        )}
                                        {debt.created_at && (
                                            <span className="small text-muted mt-1">
                                                {t("created")}: {new Date(debt.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                        {debt.debt_status === "closed" && debt.closed_at && (
                                            <span className="small text-muted mt-1 d-block">
                                                {t("closedAt")}: {new Date(debt.closed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                        <span className={debt.debt_status !== "closed" ? "fw-bold text-danger" : "fw-bold"}>
                                            {debt.debt_total} PLN
                                        </span>
                                        <span className={`badge ${debt.debt_status === "closed" ? "bg-secondary" : "bg-danger"}`}>
                                            {debt.debt_status === "closed" ? t("closed") : t("debtStatusOpen")}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDriverDebtsModal(false)}>{t("close")}</Button>
                </Modal.Footer>
            </Modal>

        </Container>
        </>
    );
};

export default ExtraFunds;
